import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { isSameWeek, format, startOfISOWeek, endOfISOWeek } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';

import { Button, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { Popover, PopoverTrigger, PopoverContent } from '@app/shared/ui/molecules/Common';

const Calendar = lazy(() => import('./Calendar'));

const getWeekRange = (date: Date) => {
  const start = startOfISOWeek(date);
  const end = endOfISOWeek(date);

  return `${format(start, 'dd.MM.yyyy')} - ${format(end, 'dd.MM.yyyy')}`;
};

interface WeekPickerProps {
  selectedDate?: Date;
  onSelectedDateChange?: (value?: Date) => void;
}

export function WeekPicker({ selectedDate, onSelectedDateChange }: WeekPickerProps) {
  const { t } = useTranslation();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className="inline-flex h-12 w-fit items-center justify-center gap-1 px-3"
        >
          <CalendarIcon size="16" />
          {selectedDate ? getWeekRange(selectedDate) : <span>{t('week_picker.placeholder')}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Suspense fallback={<LoadingSpinner containerClassName="p-3" />}>
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={selectedDate}
            onSelect={onSelectedDateChange}
            modifiers={{
              selected: (date) =>
                selectedDate ? isSameWeek(date, selectedDate, { weekStartsOn: 1 }) : false,
            }}
            showWeekNumber
            weekStartsOn={1}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}
